import { useState, useEffect } from "react";

const BREAKPOINTS = {
  mobile: 768,
  tablet: 1024,
  desktop: 1440,
};

function getScreenType(width) {
  if (width < BREAKPOINTS.mobile) return "mobile";
  if (width < BREAKPOINTS.tablet) return "tablet";
  if (width < BREAKPOINTS.desktop) return "desktop";
  return "large";
}

/**
 * Hook providing responsive window dimensions and screen type.
 *
 * Returns:
 *   width, height — current viewport size
 *   screenType — "mobile" | "tablet" | "desktop" | "large"
 *   isMobile, isTablet, isDesktop — boolean helpers
 */
export default function useWindowDimensions() {
  const [dimensions, setDimensions] = useState({
    width: window.innerWidth,
    height: window.innerHeight,
  });

  useEffect(() => {
    let rafId;
    const handleResize = () => {
      cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(() => {
        setDimensions({
          width: window.innerWidth,
          height: window.innerHeight,
        });
      });
    };

    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
      cancelAnimationFrame(rafId);
    };
  }, []);

  const screenType = getScreenType(dimensions.width);

  return {
    ...dimensions,
    screenType,
    isMobile: screenType === "mobile",
    isTablet: screenType === "tablet",
    isDesktop: screenType === "desktop" || screenType === "large",
  };
}

export { BREAKPOINTS };
